// styles.js
// Shared styles for ViewMyListings components

import { COLORS, LAYOUT, TYPOGRAPHY, LOADING_STYLES } from '../../constants';

export const styles = {
  // Page Container - Full width with proper spacing
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: COLORS.grayLight,
    padding: `${LAYOUT.spacing.xxxl} ${LAYOUT.spacing.lg}`,
  },

  // Content Wrapper - Centered with max width
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
  },

  // Header Section
  headerSection: {
    marginBottom: LAYOUT.spacing.xxxl,
  },

  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: LAYOUT.spacing.lg,
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.lg,
  },

  headerIconWrapper: {
    width: '64px',
    height: '64px',
    borderRadius: LAYOUT.borderRadius.lg,
    backgroundColor: COLORS.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${COLORS.primary}`,
    color: COLORS.primary,
  },

  pageTitle: {
    ...TYPOGRAPHY.heading1,
    color: COLORS.primary,
    fontSize: '2.25rem',
    fontWeight: '700',
    marginBottom: LAYOUT.spacing.xs,
  },

  pageSubtitle: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '1rem',
    marginBottom: 0,
  },

  addListingButton: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    color: COLORS.white,
    backgroundColor: COLORS.primary,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 12px ${COLORS.shadow}`,
  },

  // Filters Section
  filtersSection: {
    marginBottom: LAYOUT.spacing.xl,
  },

  // Main Content
  mainContent: {
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xl,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${COLORS.border}`,
  },

  // Stats Bar
  statsBar: {
    padding: `${LAYOUT.spacing.md} 0`,
    marginBottom: LAYOUT.spacing.xl,
    borderBottom: `2px solid ${COLORS.grayLight}`,
  },

  statsText: {
    fontSize: '0.9375rem',
    fontWeight: '500',
    color: COLORS.text.secondary,
  },

  // Listings Grid
  listingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: LAYOUT.spacing.xl,
  },

  listingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.md,
    height: '100%',
  },

  // Actions Container
  actionsContainer: {
    display: 'flex',
    gap: LAYOUT.spacing.sm,
    marginTop: 'auto',
  },

  soldButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: COLORS.white,
    backgroundColor: '#22c55e',
    border: `2px solid #22c55e`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.2)',
    minHeight: '36px',
  },

  editButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: COLORS.primary,
    backgroundColor: COLORS.white,
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 2px 8px ${COLORS.primary}20`,
    minHeight: '36px',
  },

  deleteButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#9d0d1d',
    backgroundColor: COLORS.white,
    border: '2px solid #9d0d1d',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(157, 13, 29, 0.2)',
    minHeight: '36px',
  },

  viewButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    backgroundColor: COLORS.grayLight,
    border: `2px solid ${COLORS.text.secondary}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    minHeight: '36px',
  },

  contactButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: COLORS.white,
    backgroundColor: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 2px 8px ${COLORS.primary}30`,
    minHeight: '36px',
  },

  archiveButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '8px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: COLORS.text.secondary,
    backgroundColor: COLORS.white,
    border: `2px solid ${COLORS.text.muted}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    minHeight: '36px',
  },

  // Empty State
  emptyStateContainer: {
    textAlign: 'center',
    padding: `${LAYOUT.spacing.huge} ${LAYOUT.spacing.lg}`,
  },

  emptyStateIcon: {
    color: COLORS.text.muted,
    marginBottom: LAYOUT.spacing.xl,
    display: 'flex',
    justifyContent: 'center',
  },

  emptyStateTitle: {
    ...TYPOGRAPHY.heading2,
    color: COLORS.text.primary,
    fontSize: '1.75rem',
    fontWeight: '600',
    marginBottom: LAYOUT.spacing.md,
  },

  emptyStateText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '1.125rem',
    maxWidth: '500px',
    margin: `0 auto ${LAYOUT.spacing.xl}`,
  },

  emptyStateButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    padding: '14px 28px',
    fontSize: '1rem',
    fontWeight: '600',
    color: COLORS.white,
    backgroundColor: COLORS.primary,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 12px ${COLORS.shadow}`,
  },

  // Loading States
  loadingContainer: LOADING_STYLES.container,
  loadingSpinner: LOADING_STYLES.spinner,
  loadingText: LOADING_STYLES.text,

  // No Results State (for filtered results)
  noResultsContainer: {
    textAlign: 'center',
    padding: `${LAYOUT.spacing.huge} ${LAYOUT.spacing.lg}`,
  },

  noResultsIcon: {
    fontSize: '4rem',
    marginBottom: LAYOUT.spacing.xl,
    opacity: 0.5,
  },

  noResultsTitle: {
    ...TYPOGRAPHY.heading2,
    color: COLORS.text.primary,
    fontSize: '1.75rem',
    fontWeight: '600',
    marginBottom: LAYOUT.spacing.md,
  },

  noResultsText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '1.125rem',
    maxWidth: '500px',
    margin: `0 auto ${LAYOUT.spacing.xl}`,
  },

  clearFiltersButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    color: COLORS.primary,
    backgroundColor: COLORS.white,
    border: `2px solid ${COLORS.primary}`,
    borderRadius: LAYOUT.borderRadius.md,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 4px 12px ${COLORS.shadow}`,
  },
};